import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Layout } from "../components/Layout";
import { TypeBadge } from "../components/Disclaimer";
import { Download, Play, Archive, Bookmark, Ruler, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Saved() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [pins, setPins] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [tab, setTab] = useState("sessions");

  const load = async () => {
    const [s, p, e] = await Promise.all([api.get("/sessions"), api.get("/pins"), api.get("/estimates")]);
    setSessions(s.data); setPins(p.data); setEstimates(e.data);
  };
  useEffect(() => { load(); }, []);

  const download = async (sid) => {
    const { data } = await api.get(`/sessions/${sid}/text`);
    const blob = new Blob([data.text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = data.filename; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded .txt");
  };
  const activate = async (sid) => { await api.post(`/sessions/${sid}/activate`); nav(`/session/${sid}`); };
  const remove = async (sid) => {
    if (!window.confirm("Delete this path for good? This cannot be undone.")) return;
    try { await api.delete(`/sessions/${sid}`); setSessions((s) => s.filter((x) => x.id !== sid)); toast.success("Path deleted"); }
    catch { toast.error("Could not delete"); }
  };
  const removePin = async (pid) => {
    if (!window.confirm("Delete this pin?")) return;
    try { await api.delete(`/pins/${pid}`); setPins((s) => s.filter((x) => x.id !== pid)); toast.success("Pin deleted"); }
    catch { toast.error("Could not delete"); }
  };
  const removeEstimate = async (eid) => {
    if (!window.confirm("Delete this estimate?")) return;
    try { await api.delete(`/estimates/${eid}`); setEstimates((s) => s.filter((x) => x.id !== eid)); toast.success("Estimate deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const TABS = [["sessions", "Sessions"], ["pins", "Pins"], ["estimates", "Estimates"]];

  return (
    <Layout>
      <div className="fadeup space-y-5">
        <h2 className="font-head font-bold text-2xl sm:text-3xl">Saved</h2>
        <div className="flex gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className="badge" data-testid={`saved-tab-${k}`}
              style={{ background: tab === k ? "var(--text)" : "transparent", color: tab === k ? "#fff" : "var(--text)" }}>{label}</button>
          ))}
        </div>

        {tab === "sessions" && (
          <div className="space-y-3" data-testid="saved-sessions">
            {sessions.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No sessions yet.</p>}
            {sessions.map((s) => (
              <div key={s.id} className="pm-card p-4" data-testid={`saved-session-${s.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="badge" style={{ background: s.status === "active" ? "var(--earn)" : "var(--text-2)" }}>{s.status}</span>
                  <span className="label-mono">{s.source} · {s.placeSnapshot?.city}</span>
                </div>
                <h3 className="font-head font-semibold text-lg mt-1">{s.title}</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{s.summary}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => activate(s.id)} className="pm-btn-ghost text-sm flex items-center gap-1.5" data-testid={`reopen-${s.id}`}><Play className="w-4 h-4" /> {s.status === "active" ? "Open" : "Reopen"}</button>
                  <button onClick={() => download(s.id)} className="pm-btn-ghost text-sm flex items-center gap-1.5" data-testid={`download-${s.id}`}><Download className="w-4 h-4" /> Download .txt</button>
                  <button onClick={() => remove(s.id)} className="pm-btn-ghost text-sm flex items-center gap-1.5" style={{ borderColor: "var(--warning)", color: "var(--warning)" }} data-testid={`delete-${s.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "pins" && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="saved-pins">
            {pins.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No pins yet. Scan something and pin it.</p>}
            {pins.map((p) => (
              <div key={p.id} className="pm-card p-4" data-testid={`pin-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 label-mono"><Bookmark className="w-4 h-4" /> {p.place}</div>
                  <button onClick={() => removePin(p.id)} data-testid={`del-pin-${p.id}`}><Trash2 className="w-4 h-4" style={{ color: "var(--warning)" }} /></button>
                </div>
                <div className="font-semibold mt-1">{p.seenText}</div>
                <div className="mt-2 space-y-1">
                  {(p.ideas || []).slice(0, 3).map((idea, i) => (
                    <div key={i} className="text-sm flex items-center gap-2"><TypeBadge type={idea.type} /> {idea.title}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "estimates" && (
          <div className="space-y-3" data-testid="saved-estimates">
            {estimates.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No estimates yet. Measure something.</p>}
            {estimates.map((e) => (
              <div key={e.id} className="pm-card p-4" data-testid={`estimate-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 label-mono"><Ruler className="w-4 h-4" /> {e.place} · {e.jobType}</div>
                  <button onClick={() => removeEstimate(e.id)} data-testid={`del-estimate-${e.id}`}><Trash2 className="w-4 h-4" style={{ color: "var(--warning)" }} /></button>
                </div>
                <div className="font-semibold mt-1">{e.seen}</div>
                <div className="text-sm mt-1 font-mono">
                  Net DIY: ${e.moneyTheyCouldMake?.netDiy?.low}–${e.moneyTheyCouldMake?.netDiy?.high} · Hours: {e.costToDo?.diyHours?.low}–{e.costToDo?.diyHours?.high}
                </div>
                {e.waste?.likely && <div className="text-xs mt-1" style={{ color: "var(--warning)" }}>Waste flagged: {e.waste.why}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
